import { MacroGridConstant } from '../constants';
import {
  CategoryDetail,
  MacroGrid,
  MacroGridItem,
  MacroGridItemType,
  PriceType
} from '../models';

// OPTIMIZATION: Reusable empty item constant (eliminates repeated object creation)
const EMPTY_ITEM: MacroGridItem = Object.freeze({
  id: 0,
  name: null,
  type: MacroGridItemType.EMPTY
});

export class MacroHelper {
  static filterMacroGridItems(
    macroGrid: MacroGrid,
    macroGridItems: MacroGridItem[],
    lazyLoadSubpages = true // Enable lazy loading by default
  ): (MacroGridItem | MacroGrid)[] {
    if (!macroGridItems) {
      return [];
    }

    // OPTIMIZATION: Single-pass filter instead of map + compact
    const values: (MacroGridItem | MacroGrid)[] = [];
    for (const macroGridItem of macroGridItems) {
      if (
        macroGridItem.name ||
        macroGridItem.category_details ||
        macroGridItem.product_details ||
        macroGridItem.modifier_details ||
        macroGridItem.child_items
      ) {
        // OPTIMIZATION: Only set label if name is missing (avoid unnecessary object creation)
        values.push(
          macroGridItem.name ? macroGridItem : setGridItemLabel(macroGridItem)
        );
      }
    }

    // OPTIMIZATION: Native sort instead of lodash sortBy
    values.sort((a, b) => (a.sort || 0) - (b.sort || 0));

    const result: (MacroGridItem | MacroGrid)[] = [];
    values.forEach((item, index) => {
      // OPTIMIZATION: Handle frozen objects - create shallow copy if needed
      if (
        'product_details' in item &&
        item.product_details &&
        !item.product_details.price_type
      ) {
        item = {
          ...item,
          product_details: {
            ...item.product_details,
            price_type: PriceType.FIXED
          }
        };
      }

      const nextItem = values[index + 1];
      if (nextItem && nextItem.sort > 16) {
        return;
      }

      if (item.sort !== 1 && index === 0) {
        result.push(EMPTY_ITEM);
      }

      if (
        item.type === MacroGridConstant.MACRO_GRID_ITEM_TYPE.SUBPAGE &&
        'child_items' in item
      ) {
        if (lazyLoadSubpages) {
          // LAZY MODE: Don't process children yet
          const macro: MacroGrid = {
            id: item.id,
            name: item.name,
            type: macroGrid.type,
            sort: item.sort,
            color: item.color,
            image: item.image,
            _isProcessed: false,
            // Store raw children for later processing
            macro_grid_items: item.child_items as MacroGridItem[]
          };
          item = macro;
        } else {
          // EAGER MODE: Process children immediately (existing behavior)
          const macro: MacroGrid = {
            id: item.id,
            name: item.name,
            type: macroGrid.type,
            sort: item.sort,
            color: item.color,
            image: item.image,
            _isProcessed: true
          };
          macro.macro_grid_items = MacroHelper.filterMacroGridItems(
            macro,
            item.child_items,
            false // Recursively process all nested children
          ) as MacroGridItem[];
          item = macro;
        }
      }
      result.push(item);
      // OPTIMIZATION: Reuse frozen EMPTY_ITEM constant
      if (nextItem && item.sort !== nextItem.sort - 1) {
        const length = nextItem.sort - item.sort;
        for (let i = 1; i < length; i++) {
          result.push(EMPTY_ITEM);
        }
      }
    });

    return result;
  }

  /**
   * Process a subpage's children on-demand
   * @param subpage The subpage MacroGrid to process
   * @returns Processed subpage with transformed children
   */
  static processSubpageLazy(subpage: MacroGrid): MacroGrid {
    // Already processed?
    if (subpage._isProcessed) {
      return subpage;
    }

    // Process children now
    const processedItems = MacroHelper.filterMacroGridItems(
      subpage,
      subpage.macro_grid_items as MacroGridItem[],
      false // Eagerly process all nested children
    );

    return {
      ...subpage,
      macro_grid_items: processedItems as MacroGridItem[],
      _isProcessed: true
    };
  }

  static getCategoriesFromMacroGridItems(
    items: MacroGridItem[]
  ): CategoryDetail[] {
    const categories: CategoryDetail[] = [];

    items?.forEach((item) => {
      if (item.category_details) {
        if (
          !categories.find(
            (category) =>
              category.category_id === item.category_details.category_id
          )
        ) {
          categories.push(item.category_details);
        }
      } else {
        categories.push(
          ...MacroHelper.getCategoriesFromMacroGridItems(
            item.child_items || (item as any).macro_grid_items
          ).filter(
            (childCategory) =>
              !categories.find(
                (category) => category.category_id === childCategory.category_id
              )
          )
        );
      }
    });

    return categories;
  }
}

/** Set the label (and its translations/overrides) for the given macro grid item */
function setGridItemLabel(macroGridItem: MacroGridItem): MacroGridItem {
  let { name } = macroGridItem;
  let name_translations: MacroGridItem['name_translations'];
  let overwritten_names: MacroGridItem['overwritten_names'];
  let overwritten_names_translations: MacroGridItem['overwritten_names_translations'];

  if (!name) {
    const details =
      macroGridItem.category_details || macroGridItem.product_details;
    name = details?.name;
    name_translations = details?.name_translations;
    overwritten_names = macroGridItem.category_details?.overwritten_names;
    overwritten_names_translations =
      macroGridItem.category_details?.overwritten_names_translations;
  }

  // OPTIMIZATION: Create shallow copy if name needs to be set
  // This handles frozen/read-only objects from API responses
  if (!macroGridItem.name && name) {
    return {
      ...macroGridItem,
      name,
      name_translations,
      overwritten_names,
      overwritten_names_translations
    };
  }

  return macroGridItem;
}
